import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { T } from "./tokens";
import { Panel, StatusDot } from "./ui";
import { HsAdminGate } from "./HsAdminGate";
import { initials, utilColour } from "./ConsultantsScreen";
import { dayCellsFor, fetchConsultantVisits, useTeamFilter, useTeamOf, toISODate, addDays, sameDay, roomForSecond, scoreConsultantPeriod, daysUsage, dayExceptionFor } from "./data";
import {
    fmtPoints,
    fieldDaysPerMonth,
    availablePoints,
    classify,
    VISIT_TYPE_LABEL,
} from "./points";
import { usePointsConfig } from "./pointsConfig";
import { monthlyVisitTarget } from "./points";
import type { WeeklyVisitSummary, VisitConsultantT, SfVisit } from "./data";

/**
 * The performance report — replaces the manual spreadsheet tally (requirement A8).
 * Per consultant for the selected period: points, physical and remote visits,
 * leave and admin days, booked days, and utilisation in both days and points.
 */

// Proportional so the table fills the panel and never needs a scrollbar; the
// name column is capped rather than greedy.
// Track count must match the header array below, so both are built from the
// same flag: with points hidden the util-points and points-goal tracks go.
const gridFor = (withPoints: boolean) =>
    [
        "minmax(150px, 1.6fr)",
        "minmax(0, 0.72fr)", // booked
        "minmax(0, 0.84fr)", // util · days
        ...(withPoints ? ["minmax(0, 0.84fr)"] : []), // util · pts
        "minmax(0, 0.92fr)", // visits / goal
        "minmax(0, 1.15fr)", // physical (carries "+n unflagged")
        "minmax(0, 0.72fr)", // remote
        "minmax(0, 0.6fr)", // leave
        "minmax(0, 0.6fr)", // admin
        ...(withPoints ? ["minmax(0, 0.92fr)"] : []), // missed doubles
        ...(withPoints ? ["minmax(0, 1.15fr)"] : []), // points / goal
    ].join(" ");

interface Row {
    teamName: string;
    c: VisitConsultantT;
    booked: number | null;
    util: number | null;
    physical: number | null; // null = not loaded (still loading, or the query failed)
    remote: number | null;
    uncategorised: number | null; // visits with no On Site / Remote flag set
    failed: boolean; // the visit query errored — the blanks are unknown, not zero
    leave: number;
    admin: number;
    missedDoubles: number | null;
    points: number | null;
    /** Visits scored off an account with no industry. They DO carry points (the
     *  agreed default), but the account still needs fixing — this is the chase
     *  list, not a scoring gap. */
    industryMissing: number | null;
    /** Capacity points — the day cost, excluding the report supplement. This is
     *  what utilisation is measured on. */
    capacity: number | null;
    /** Total visits in the period, for the old visits-against-15 reporting. */
    visits: number | null;
}

// "…" = not loaded yet, or the query failed. Never render a placeholder 0 —
// a fabricated zero reads as a measured fact about a named person.
const fmtMins = (m?: number | null) => {
    if (m == null || !isFinite(m) || m <= 0) return "—";
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${String(Math.round(m % 60)).padStart(2, "0")}m` : `${Math.round(m)}m`;
};

const median = (xs: number[]) => {
    if (xs.length === 0) return null;
    const a = [...xs].sort((x, y) => x - y);
    return a[Math.floor(a.length / 2)];
};

const Stat = ({ value, label, tip, color }: { value: string | number; label: string; tip?: string; color?: string }) => (
    <Box title={tip} sx={{ background: T.rowTint, border: `1px solid ${T.borderSoft}`, borderRadius: "8px", padding: "10px 11px" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 800, color: color || T.ink2, fontVariantNumeric: "tabular-nums" }}>
            {value}
        </Typography>
        <Typography sx={{ fontSize: "10.5px", color: T.muted, lineHeight: 1.3 }}>{label}</Typography>
    </Box>
);

const BarRow = ({ label, value, max, color, width = 150 }: { label: string; value: number | string; max: number; color?: string; width?: number }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "4px 0" }}>
        <Typography sx={{ fontSize: "11.5px", color: T.muted, width: `${width}px`, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {label}
        </Typography>
        <Box sx={{ flexGrow: 1, height: "9px", borderRadius: "5px", background: T.rowTint, overflow: "hidden" }}>
            <Box sx={{ width: `${max > 0 ? (Number(value) / max) * 100 : 0}%`, height: "100%", background: color || T.purple }} />
        </Box>
        <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: T.ink2, width: "50px", textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
            {value}
        </Typography>
    </Box>
);

const num = (v: number | null) => (v == null ? "…" : String(v));

// Every list on this screen shows 10 rows and expands on demand — the tables
// were dominating the page at 85 consultants.
const ShowAllButton = ({ expanded, total, onToggle }: { expanded: boolean; total: number; onToggle: () => void }) => (
    <Box
        component="button"
        onClick={onToggle}
        sx={{
            border: "none",
            background: "transparent",
            color: T.purpleDeep,
            fontSize: "11.5px",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: T.font,
            padding: "8px 0 0",
            display: "block",
        }}
    >
        {expanded ? "▲ Show top 10 only" : `▼ Show all ${total}`}
    </Box>
);

const buildCsv = (
    rows: Row[],
    periodPointsForCsv: number,
    goalFor: (jobTitle?: string) => number,
    withPoints: boolean,
) => {
    // Header, row and notes are built from the same flag so they cannot drift.
    const header = [
        "Consultant", "Team", "Booked days", "Utilisation % (days)",
        ...(withPoints ? ["Utilisation % (points)"] : []),
        "Visits", "Visits target", "Physical visits", "Remote visits", "Unflagged visits",
        "Leave days", "Admin days",
        ...(withPoints ? ["Pairing candidates"] : []),
        ...(withPoints ? ["Recognition points", "Capacity points"] : []),
        "Visits on accounts with no industry",
    ].join(",");
    const lines = rows.map((r) =>
        [
            `"${r.c.name}"`,
            `"${r.teamName}"`,
            r.booked ?? "",
            r.util ?? "",
            ...(withPoints
                ? [r.capacity == null ? "" : Math.round((r.capacity / periodPointsForCsv) * 100)]
                : []),
            r.visits ?? "",
            goalFor(r.c.jobTitle),
            r.physical ?? "",
            r.remote ?? "",
            r.uncategorised ?? "",
            r.leave,
            r.admin,
            ...(withPoints ? [r.missedDoubles ?? ""] : []),
            ...(withPoints ? [r.points ?? "", r.capacity ?? ""] : []),
            r.industryMissing ?? "",
        ].join(","),
    );

    // The caveats travel with the file. Once this is emailed on or pasted into a
    // deck, nothing else tells the reader what the numbers do not account for.
    const notes = [
        "",
        "# Utilisation (days): days used (a visit, an admin day or a training day) against working days in the period, with holiday and sickness taken out. A half-day visit still uses the day. Booked days is the same numerator.",
        ...(withPoints
            ? [
                  "# Utilisation (points) is capacity points against points available (field days x 2), so a half-day visit counts as half a day.",
                  "# Recognition points: visits at the same account on the same day form one occasion, scoring the highest rate plus a supplement per extra assessment.",
                  "# Capacity points exclude the per-report supplement - that is the day cost, and what utilisation (points) is measured on.",
              ]
            : []),
        "# Visits target is the monthly target for the consultant's job title (15, 10 or 4), pro-rated for a week.",
        "# Visits on accounts with no industry DID score, using the agreed default. The account still needs its industry set in Salesforce.",
        "# Fire assessments at High Risk sites are under-scored: no fire-risk field exists yet.",
        ...(withPoints
            ? ["# Pairing candidates are single-visit days booked for 5h or less. Travel and location are not assessed, so they are not confirmed missed doubles."]
            : []),
    ];
    return [header, ...lines, ...notes].join("\r\n");
};

const ReportsScreen = ({
    data,
    isLoading,
    isError,
    team = "AllTeams",
    consultant = "AllConsultants",
    dates,
}: {
    data: WeeklyVisitSummary | null | undefined;
    isLoading: boolean;
    isError: boolean;
    team?: string;
    consultant?: string;
    dates: Date[];
}) => {
    const { config, showPoints } = usePointsConfig();
    const inTeam = useTeamFilter(team);
    const teamOf = useTeamOf();
    const [tab, setTab] = useState<"table" | "metrics">("table");
    const [showAllPoints, setShowAllPoints] = useState(false);
    const [showAllUtil, setShowAllUtil] = useState(false);
    const [showAllMix, setShowAllMix] = useState(false);
    const [showAllTable, setShowAllTable] = useState(false);
    const weekDates = dates; // 5 weekdays (week view) or every weekday of the month

    // Filter BEFORE the per-consultant queries below, so a narrowed team does
            // not still fetch visits for everyone.
    const consultants = (data?.regions || [])
        .flatMap((r) => (r.consultants || []).map((c) => ({ teamName: teamOf(c.email), c })))
        .filter(({ c }) => inTeam(c.email))
        .filter(
            ({ c }) =>
                consultant === "AllConsultants" ||
                (c.email || "").toLowerCase() === consultant.toLowerCase(),
        );

    const from = toISODate(weekDates[0]);
    const to = toISODate(addDays(weekDates[weekDates.length - 1], 1));

    // Pro-rated for a week by field days per year, not by dividing the month by
    // four: 7/week annualises to 364 against 30/month's 360.
    const isWeek = weekDates.length <= 5;
    const weeklyTarget = (config.monthlyPointsTarget * 12) / 52;
    const pointsGoal = Math.round((isWeek ? weeklyTarget : config.monthlyPointsTarget) * 10) / 10;

    // Utilisation is reported in points as well as days. 15 field days a month at
    // 2 points a day is 30 points, so full utilisation and the target coincide.
    const periodFieldDays = isWeek ? config.fieldDaysPerYear / 52 : fieldDaysPerMonth(config);
    const periodPoints = availablePoints(periodFieldDays, config);
    const visitsGoal = Math.round(periodFieldDays * 10) / 10;
    // Target varies by grade (15 / 10 / 4); this report measured everyone against
    // field days, which is 15 — right for a consultant, wrong for the rest.
    const visitsGoalFor = (jobTitle?: string) => {
        const monthly = monthlyVisitTarget(jobTitle);
        if (monthly == null) return visitsGoal;
        return Math.round((isWeek ? (monthly * 12) / 52 : monthly) * 10) / 10;
    };
    const pointsColour = (pts: number | null) =>
        pts == null ? T.muted2 : pts >= pointsGoal ? T.green : pts >= pointsGoal * 0.7 ? T.purpleDeep : T.amber;

    // One visits query per consultant. Same query
    // keys as the Schedule rows, so results are shared via the React Query cache.
    const visitQueries = useQueries({
        queries: consultants.map(({ c }) => ({
            queryKey: ["tandem", "consultant-visits", from, to, c.email],
            queryFn: () => fetchConsultantVisits(from, to, c.email),
            enabled: !!c.email,
            staleTime: 1000 * 60 * 5,
        })),
    });

    // Hooks must all run before the early returns below, or React sees a
    // different number of them between renders and throws.
    const allVisits: SfVisit[] = visitQueries.flatMap((q) => (q?.data as SfVisit[] | undefined) ?? []);

    // Aggregate view over the same data the table uses — no extra fetches.
    const agg = useMemo(() => {
        const delivered = allVisits.filter((v) => v.visitStartDate);
        const mix = new Map<string, number>();
        const industries = new Map<string, number>();
        const statuses = new Map<string, number>();
        delivered.forEach((v) => {
            const t = VISIT_TYPE_LABEL[classify(v.subActivity)];
            mix.set(t, (mix.get(t) || 0) + 1);
            const ind = v.industry || "not set";
            industries.set(ind, (industries.get(ind) || 0) + 1);
            const st = v.status || "—";
            statuses.set(st, (statuses.get(st) || 0) + 1);
        });

        const onSiteMins = delivered.map((v) => v.attendanceMins).filter((m): m is number => m != null && m > 0);
        const travel = delivered
            .map((v) => {
                const t0 = v.travelStartTime ? new Date(v.travelStartTime).getTime() : NaN;
                const t1 = v.travelEndTime ? new Date(v.travelEndTime).getTime() : NaN;
                const a0 = v.actualStartTime ? new Date(v.actualStartTime).getTime() : NaN;
                const a1 = v.actualEndTime ? new Date(v.actualEndTime).getTime() : NaN;
                if (![t0, t1, a0, a1].every(isFinite)) return null;
                return (t1 - t0 - (a1 - a0)) / 60000;
            })
            .filter((m): m is number => m != null && m > 0);
        const writeUp = delivered
            .map((v) => (v.writeUpTimeMinutes == null ? null : Number(v.writeUpTimeMinutes)))
            .filter((m): m is number => m != null && m > 0);

        return {
            visits: delivered.length,
            mix: [...mix.entries()].sort((a, b) => b[1] - a[1]),
            industries: [...industries.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8),
            statuses: [...statuses.entries()].sort((a, b) => b[1] - a[1]),
            onSite: delivered.filter((v) => v.visitCategory !== "Remote").length,
            remote: delivered.filter((v) => v.visitCategory === "Remote").length,
            medOnSite: median(onSiteMins),
            medTravel: median(travel),
            medWriteUp: median(writeUp),
            travelSample: travel.length,
            noIndustry: delivered.filter((v) => !v.industry).length,
        };
    }, [allVisits]);

    if (isLoading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
                <CircularProgress size={26} />
            </Box>
        );
    if (isError) return <Box sx={{ p: "28px", color: T.muted }}>Couldn't load the report data.</Box>;
    if (!data) return <HsAdminGate />;
    if (consultants.length === 0)
        return <Box sx={{ p: "28px", color: T.muted }}>No consultants for this team / week.</Box>;

    const rows: Row[] = consultants.map(({ teamName, c }, i) => {
        const visits = visitQueries[i]?.data as SfVisit[] | undefined;
        const weekVisits = (visits || []).filter(
            (v) => v.visitStartDate && weekDates.some((d) => sameDay(new Date(v.visitStartDate as string), d)),
        );
        // Only real data counts as loaded. Treating isError as loaded turned a
        // timeout into "0 visits", indistinguishable from booking nothing.
        const loaded = !!visitQueries[i]?.data;
        const failed = !!visitQueries[i]?.isError;

        // Three buckets, not two: an unset category is not evidence of an on-site
        // visit, so it is counted separately instead of inflating Physical.
        const physical = loaded ? weekVisits.filter((v) => v.visitCategory === "On Site").length : null;
        const remote = loaded ? weekVisits.filter((v) => v.visitCategory === "Remote").length : null;
        const uncategorised = loaded
            ? weekVisits.filter((v) => v.visitCategory !== "On Site" && v.visitCategory !== "Remote").length
            : null;

        const cells = weekDates.map((d) => dayCellsFor(c, d));
        // By exception TYPE, not comment text — a Holiday commented "Annual leave" is still holiday.
        const leave = cells.filter((cell) => cell?.comment && (cell.dayStatus === "H" || cell.dayStatus === "S")).length;
        const admin = cells.filter((cell) => cell?.comment && cell.dayStatus === "A").length;

        // Single-visit days with room for a second — missed doubles.
        const missedDoubles = loaded
            ? weekDates.filter((d) => {
                  if (dayCellsFor(c, d)?.comment) return false;
                  const dv = weekVisits.filter((v) => v.visitStartDate && sameDay(new Date(v.visitStartDate as string), d));
                  return roomForSecond(dv);
              }).length
            : null;

        // Points come from the agreed model — occasions, then recognition score.
        // Shared via scoreConsultantPeriod so this and Consultants cannot drift.
        const scored = loaded ? scoreConsultantPeriod(weekVisits, config) : null;
        const usage = daysUsage(weekDates, weekVisits, (d) => dayExceptionFor(c, d));
        const points = scored?.points ?? null;
        const capacity = scored?.capacity ?? null;

        return {
            teamName,
            c,
            booked: loaded ? usage.used : null,
            util: loaded ? usage.pct : null,
            physical,
            remote,
            uncategorised,
            failed,
            leave,
            admin,
            missedDoubles,
            points,
            industryMissing: scored?.industryMissing ?? null,
            capacity,
            visits: loaded ? weekVisits.length : null,
        };
    });

    const totals = rows.reduce(
        (t, r) => ({
            booked: t.booked + (r.booked ?? 0),
            physical: t.physical + (r.physical ?? 0),
            remote: t.remote + (r.remote ?? 0),
            leave: t.leave + r.leave,
            admin: t.admin + r.admin,
            missedDoubles: t.missedDoubles + (r.missedDoubles ?? 0),
            points: t.points + (r.points ?? 0),
            capacity: t.capacity + (r.capacity ?? 0),
            visits: t.visits + (r.visits ?? 0),
        }),
        { booked: 0, physical: 0, remote: 0, leave: 0, admin: 0, missedDoubles: 0, points: 0, capacity: 0, visits: 0 },
    );

    const exportCsv = () => {
        const blob = new Blob([buildCsv(rows, periodPoints, visitsGoalFor, showPoints)], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `tandem-report-${from}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const TABS = [
        { key: "table", label: "Per consultant" },
        { key: "metrics", label: "Metrics" },
    ] as const;

    // How the team sits against the target, rather than who is top.
    const scored = rows.filter((r) => r.points != null).map((r) => r.points as number);
    const atTarget = scored.filter((x) => x >= pointsGoal).length;
    const closeToTarget = scored.filter((x) => x >= pointsGoal * 0.7 && x < pointsGoal).length;
    const wellShort = scored.filter((x) => x < pointsGoal * 0.7).length;
    const atZero = scored.filter((x) => x === 0).length;
    const medPoints = scored.length ? [...scored].sort((a, b) => a - b)[Math.floor(scored.length / 2)] : null;
    const teamUtil = rows.length ? Math.round((totals.capacity / (rows.length * periodPoints)) * 100) : null;
    // Visit-based equivalents, used when points are hidden. Measured against each
    // consultant's own grade target rather than one figure for the whole team.
    const visitsAtTarget = rows.filter(
        (r) => r.visits != null && r.visits >= visitsGoalFor(r.c.jobTitle),
    ).length;
    const visitCounts = rows.filter((r) => r.visits != null).map((r) => r.visits as number);
    const medVisits = visitCounts.length
        ? [...visitCounts].sort((a, b) => a - b)[Math.floor(visitCounts.length / 2)]
        : null;

    const OverallMetrics = (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))", gap: "10px" }}>
            <Stat
                value={`${showPoints ? atTarget : visitsAtTarget}/${rows.length}`}
                label={showPoints ? `Reached ${pointsGoal} points` : "Reached their visit target"}
                color={(showPoints ? atTarget : visitsAtTarget) > 0 ? T.green : undefined}
                tip={
                    showPoints
                        ? "Consultants at or above the target for this period"
                        : "Consultants at or above the visit target for their grade this period"
                }
            />
            {showPoints && (
                <Stat value={closeToTarget} label={`Within 30% of ${pointsGoal}`} color={T.purpleDeep} />
            )}
            {showPoints && (
                <Stat value={wellShort} label="More than 30% short" color={wellShort > 0 ? T.amber : undefined} />
            )}
            <Stat value={atZero} label="Nothing recorded" color={atZero > 0 ? T.red : undefined} tip="Nothing recorded this period — check before reading anything into it" />
            {showPoints ? (
                <Stat value={medPoints == null ? "—" : fmtPoints(medPoints)} label="Median points" tip="Half the team is above this, half below" />
            ) : (
                <Stat value={medVisits == null ? "—" : medVisits} label="Median visits" tip="Half the team is above this, half below" />
            )}
            {showPoints && (
                <Stat value={teamUtil == null ? "—" : `${teamUtil}%`} label="Utilisation · points" color={T.purple} tip={`Capacity against ${periodPoints} available per consultant`} />
            )}
        </Box>
    );

    return (
        <Box sx={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "18px" }}>
            <Box sx={{ display: "flex", borderBottom: `1px solid ${T.border}` }}>
                {TABS.map((t) => (
                    <Box
                        key={t.key}
                        component="button"
                        onClick={() => setTab(t.key)}
                        sx={{
                            border: "none",
                            borderBottom: `2px solid ${tab === t.key ? T.purple : "transparent"}`,
                            background: "transparent",
                            color: tab === t.key ? T.ink2 : T.muted,
                            padding: "9px 16px",
                            fontSize: "13px",
                            fontWeight: 800,
                            cursor: "pointer",
                            fontFamily: T.font,
                        }}
                    >
                        {t.label}
                    </Box>
                ))}
            </Box>

            {tab === "metrics" && (
                <>
                    {OverallMetrics}

                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px" }}>
                        <Stat value={rows.length} label="Consultants" />
                        <Stat value={agg.visits} label="Visits in period" />
                        {showPoints && (
                            <Stat value={fmtPoints(totals.points)} label="Recognition points" color={T.purpleDeep} />
                        )}
                        {showPoints && <Stat value={fmtPoints(totals.capacity)} label="Capacity used" />}
                        {showPoints && (
                            <Stat value={totals.missedDoubles} label="Pairing candidates" color={totals.missedDoubles ? T.amber : undefined} />
                        )}
                    </Box>

                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: "16px", alignItems: "stretch" }}>
                        {/* Physical vs remote per consultant — moved here from the
                            per-consultant tab, which now leads with points. */}
                        <Panel
                            title="Visit mix by consultant"
                            sub="physical vs remote for the period — the tally as a picture"
                        >
                            {(() => {
                                const chartRows = rows
                                    .map((r) => ({ ...r, total: (r.physical ?? 0) + (r.remote ?? 0) }))
                                    .sort((a, b) => b.total - a.total);
                                const max = Math.max(1, ...chartRows.map((r) => r.total));
                                const shown = showAllMix ? chartRows : chartRows.slice(0, 10);
                                return (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                        {shown.map((r) => (
                                            <Box key={r.c.email} sx={{ display: "grid", gridTemplateColumns: "150px 1fr 110px", gap: "10px", alignItems: "center" }}>
                                                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {r.c.name}
                                                </Typography>
                                                <Box sx={{ display: "flex", height: "16px", borderRadius: "5px", overflow: "hidden", background: T.rowTint, border: `1px solid ${T.borderSoft}` }}>
                                                    <Box
                                                        title={`${r.physical ?? 0} physical`}
                                                        sx={{ width: `${((r.physical ?? 0) / max) * 100}%`, background: T.purpleDeep, transition: "width .3s" }}
                                                    />
                                                    <Box
                                                        title={`${r.remote ?? 0} remote`}
                                                        sx={{ width: `${((r.remote ?? 0) / max) * 100}%`, background: T.purple, opacity: 0.45, transition: "width .3s" }}
                                                    />
                                                </Box>
                                                <Typography sx={{ fontSize: "11.5px", color: T.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                                    {r.physical == null ? "…" : `${r.physical} phys · ${r.remote} rem`}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {chartRows.length > 10 && (
                                            <ShowAllButton expanded={showAllMix} total={chartRows.length} onToggle={() => setShowAllMix((v) => !v)} />
                                        )}
                                        <Box sx={{ display: "flex", gap: "16px", mt: "4px" }}>
                                            <Typography sx={{ fontSize: "11px", color: T.muted }}>
                                                <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.purpleDeep, mr: "5px", verticalAlign: "middle" }} />
                                                Physical
                                            </Typography>
                                            <Typography sx={{ fontSize: "11px", color: T.muted }}>
                                                <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.purple, opacity: 0.45, mr: "5px", verticalAlign: "middle" }} />
                                                Remote
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })()}
                        </Panel>

                        <Panel title="Visit mix" sub={`${agg.visits} visits in the period`} sx={{ height: "100%" }}>
                            {agg.mix.length === 0 ? (
                                <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing in this period.</Typography>
                            ) : (
                                agg.mix.map(([label, n]) => <BarRow key={label} label={label} value={n} max={agg.mix[0][1]} />)
                            )}
                            <Box sx={{ display: "flex", gap: "8px", mt: "12px" }}>
                                <Stat value={agg.onSite} label="On site" />
                                <Stat value={agg.remote} label="Remote" />
                            </Box>
                        </Panel>

                        <Panel title="Client mix" sub="Industries visited — sets the points rate" sx={{ height: "100%" }}>
                            {agg.industries.length === 0 ? (
                                <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing in this period.</Typography>
                            ) : (
                                agg.industries.map(([label, n]) => (
                                    <BarRow key={label} label={label} value={n} max={agg.industries[0][1]} color={label === "not set" ? T.amber : T.purple} />
                                ))
                            )}
                            {agg.noIndustry > 0 && (
                                <Typography sx={{ fontSize: "11px", color: T.amber, mt: "10px" }}>
                                    {agg.noIndustry} visits are on accounts with no industry set.
                                </Typography>
                            )}
                        </Panel>

                        <Panel title="How a visit's day is spent" sub="Median of visits with times recorded" sx={{ height: "100%" }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                                <Stat value={fmtMins(agg.medOnSite)} label="On site" />
                                <Stat value={fmtMins(agg.medTravel)} label="Travel, round trip" tip={`From ${agg.travelSample} visits with all four times`} />
                                <Stat value={fmtMins(agg.medWriteUp)} label="Write-up" />
                            </Box>
                        </Panel>

                        <Panel title="Status of work in the period" sx={{ height: "100%" }}>
                            {agg.statuses.length === 0 ? (
                                <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing in this period.</Typography>
                            ) : (
                                agg.statuses.map(([label, n]) => (
                                    <BarRow
                                        key={label}
                                        label={label}
                                        value={n}
                                        max={agg.statuses[0][1]}
                                        color={label.toLowerCase() === "completed" ? T.green : label.toLowerCase() === "closed" ? T.muted2 : T.purple}
                                    />
                                ))
                            )}
                        </Panel>
                    </Box>
                </>
            )}

            {tab === "table" && (
            <>
            {OverallMetrics}

            {/* Top 5 performers — points leaderboard for the period */}
            <Panel
                title="Top 5 performers"
                sub={
                    showPoints
                        ? `highest points this period · goal ${pointsGoal}`
                        : "most visits this period · marker = the target for their grade"
                }
            >
                {(() => {
                    const rank = (r: Row) => (showPoints ? r.points : r.visits) ?? -1;
                    const top = [...rows].sort((a, b) => rank(b) - rank(a)).slice(0, 5);
                    const maxPts = Math.max(1, ...top.map((r) => rank(r)), showPoints ? pointsGoal : 0);
                    const medal = ["🥇", "🥈", "🥉"];
                    return (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            {top.map((r, i) => (
                                <Box key={r.c.email} sx={{ display: "grid", gridTemplateColumns: "34px 1.4fr 2fr 120px 110px", gap: "10px", alignItems: "center", padding: "8px 0", borderTop: i === 0 ? "none" : `1px solid ${T.borderSoft}` }}>
                                    <Typography sx={{ fontSize: i < 3 ? "16px" : "12px", fontWeight: 800, color: T.muted, textAlign: "center" }}>
                                        {medal[i] ?? `#${i + 1}`}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                                        <Box sx={{ width: "26px", height: "26px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>
                                            {initials(r.c.name)}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {r.c.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: "10.5px", color: T.muted }}>{r.teamName}</Typography>
                                        </Box>
                                    </Box>
                                    {/* points bar with the goal marked */}
                                    {(() => {
                                        const goal = showPoints ? pointsGoal : visitsGoalFor(r.c.jobTitle);
                                        const value = showPoints ? r.points : r.visits;
                                        const colour = showPoints
                                            ? pointsColour(r.points)
                                            : value == null
                                              ? T.muted2
                                              : value >= goal
                                                ? T.green
                                                : value >= goal * 0.7
                                                  ? T.purpleDeep
                                                  : T.amber;
                                        return (
                                            <>
                                                <Box sx={{ position: "relative", height: "12px", borderRadius: "4px", background: T.rowTint, border: `1px solid ${T.borderSoft}` }}>
                                                    <Box sx={{ position: "absolute", top: "1px", bottom: "1px", left: 0, width: `${((value ?? 0) / maxPts) * 100}%`, background: colour, borderRadius: "3px", transition: "width .3s" }} />
                                                    <Box title={`target ${goal}`} sx={{ position: "absolute", top: "-2px", bottom: "-2px", width: "2px", background: T.ink2, left: `${Math.min(100, (goal / maxPts) * 100)}%` }} />
                                                </Box>
                                                <Typography sx={{ fontSize: "13px", fontWeight: 800, color: colour, fontVariantNumeric: "tabular-nums" }}>
                                                    {value == null ? "…" : showPoints ? `${value} pts` : `${value} visits`}{" "}
                                                    {value != null && value >= goal ? "✓" : ""}
                                                </Typography>
                                            </>
                                        );
                                    })()}
                                    <Typography sx={{ fontSize: "11.5px", color: T.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                        {r.physical ?? "…"} phys · {r.remote ?? "…"} rem · {r.util == null ? "—" : `${r.util}%`}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                })()}
            </Panel>

            {/* Points and utilisation, side by side — the two ways the same
                period gets read. */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px", alignItems: "stretch" }}>
                {showPoints && (
                <Panel title="Points per consultant" sub={`against ${pointsGoal} this period · marker = target`} sx={{ height: "100%" }}>
                    {rows.length === 0 ? (
                        <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing to show.</Typography>
                    ) : (
                        (() => {
                            const ranked = [...rows].sort((a, b) => (b.points ?? -1) - (a.points ?? -1));
                            const max = Math.max(pointsGoal, ...rows.map((x) => x.points ?? 0));
                            const shown = showAllPoints ? ranked : ranked.slice(0, 10);
                            return (
                                <>
                                    {shown.map((r) => (
                                        <Box key={r.c.email} sx={{ display: "grid", gridTemplateColumns: "140px 1fr 62px", gap: "10px", alignItems: "center", padding: "4px 0" }}>
                                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {r.c.name}
                                            </Typography>
                                            <Box sx={{ position: "relative", height: "14px", borderRadius: "5px", background: T.rowTint, border: `1px solid ${T.borderSoft}` }}>
                                                <Box sx={{ position: "absolute", top: "1px", bottom: "1px", left: 0, width: `${((r.points ?? 0) / max) * 100}%`, background: (r.points ?? 0) >= pointsGoal ? T.green : T.purple, borderRadius: "4px", transition: "width .3s" }} />
                                                <Box title={`target ${pointsGoal}`} sx={{ position: "absolute", top: 0, bottom: 0, width: "2px", background: T.ink2, opacity: 0.35, left: `${(pointsGoal / max) * 100}%` }} />
                                            </Box>
                                            <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: pointsColour(r.points), textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                                {r.points == null ? "…" : fmtPoints(r.points)}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {ranked.length > 10 && (
                                        <ShowAllButton expanded={showAllPoints} total={ranked.length} onToggle={() => setShowAllPoints((v) => !v)} />
                                    )}
                                    <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "10px" }}>
                                        Green = reached the {pointsGoal}-point target for the period.
                                    </Typography>
                                </>
                            );
                        })()
                    )}
                </Panel>
                )}

                <Panel
                    title={showPoints ? "Utilisation — both measures" : "Utilisation"}
                    sub={
                        showPoints
                            ? "the long-standing day count alongside the new points measure"
                            : "days used against working days in the period"
                    }
                >
                    {rows.length === 0 ? (
                        <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing to show.</Typography>
                    ) : (
                        (() => {
                            const utilPts = (r: Row) => (r.capacity == null ? null : Math.round((r.capacity / periodPoints) * 100));
                            // Order by the measure that is actually visible, or the list
                            // looks unsorted to anyone who cannot see points.
                            const rankBy = (r: Row) => (showPoints ? utilPts(r) : r.util) ?? -1;
                            const ranked = [...rows].sort((a, b) => rankBy(b) - rankBy(a));
                            const shown = showAllUtil ? ranked : ranked.slice(0, 10);
                            return (
                                <>
                                    <Box sx={{ display: "flex", gap: "16px", mb: "12px" }}>
                                        <Typography sx={{ fontSize: "11px", color: T.muted }}>
                                            <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.border, mr: "5px", verticalAlign: "middle" }} />
                                            {showPoints ? "Days · the old measure" : "Days used against working days"}
                                        </Typography>
                                        {showPoints && (
                                            <Typography sx={{ fontSize: "11px", color: T.muted }}>
                                                <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.purple, mr: "5px", verticalAlign: "middle" }} />
                                                Points · the new measure
                                            </Typography>
                                        )}
                                    </Box>
                                    {shown.map((r) => {
                                        const up = utilPts(r);
                                        return (
                                            <Box key={r.c.email} sx={{ display: "grid", gridTemplateColumns: "140px 1fr 92px", gap: "10px", alignItems: "center", padding: "5px 0" }}>
                                                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {r.c.name}
                                                </Typography>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                                                    <Box title="Utilisation · days" sx={{ height: "9px", borderRadius: "5px", background: T.rowTint, overflow: "hidden" }}>
                                                        <Box sx={{ width: `${Math.min(100, r.util ?? 0)}%`, height: "100%", background: T.border, transition: "width .3s" }} />
                                                    </Box>
                                                    {showPoints && (
                                                        <Box title="Utilisation · points" sx={{ height: "9px", borderRadius: "5px", background: T.rowTint, overflow: "hidden" }}>
                                                            <Box sx={{ width: `${Math.min(100, up ?? 0)}%`, height: "100%", background: T.purple, transition: "width .3s" }} />
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography sx={{ fontSize: "11px", color: T.muted, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                                    {r.util == null ? "—" : `${r.util}%`}
                                                    {showPoints && (
                                                        <>
                                                            {" · "}
                                                            <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 800, color: T.purpleDeep }}>
                                                                {up == null ? "…" : `${up}%`}
                                                            </Box>
                                                        </>
                                                    )}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                    {ranked.length > 10 && (
                                        <ShowAllButton expanded={showAllUtil} total={ranked.length} onToggle={() => setShowAllUtil((v) => !v)} />
                                    )}
                                    {showPoints && (
                                        <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "10px", lineHeight: 1.5 }}>
                                            The two measures differ by design — a half-day visit fills a whole day in the
                                            old count. Both are shown so they can be compared.
                                        </Typography>
                                    )}
                                </>
                            );
                        })()
                    )}
                </Panel>
            </Box>

            {/* Per-consultant performance table */}
            <Panel
                title="Consultant performance"
                sub={`${from} → ${toISODate(weekDates[weekDates.length - 1])} · visits, days and points per consultant — the manual tally, automated`}
                action={
                    <Box
                        component="button"
                        onClick={exportCsv}
                        sx={{
                            border: "none",
                            background: T.ink,
                            color: "#fff",
                            borderRadius: "8px",
                            padding: "8px 14px",
                            fontSize: "12.5px",
                            fontWeight: 700,
                            cursor: "pointer",
                            fontFamily: T.font,
                            "&:hover": { background: T.indigoDeep },
                        }}
                    >
                        ⬇ Export CSV
                    </Box>
                }
            >
                <Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), gap: "8px", background: T.rowTint, borderRadius: "8px", padding: "8px 10px", "& > *:not(:first-child)": { textAlign: "right" } }}>
                            {[
                                "Consultant",
                                "Booked",
                                "Util · days",
                                ...(showPoints ? ["Util · pts"] : []),
                                "Visits",
                                "Physical",
                                "Remote",
                                "Leave",
                                "Admin",
                                ...(showPoints ? ["Missed dbls"] : []),
                                ...(showPoints ? [`Points /${pointsGoal}`] : []),
                            ].map((h) => (
                                <Typography key={h} sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {(showAllTable ? rows : rows.slice(0, 10)).map((r) => (
                            <Box key={r.c.email} sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), gap: "8px", alignItems: "center", padding: "9px 10px", borderTop: `1px solid ${T.borderSoft}`, "& > *:not(:first-child)": { textAlign: "right" } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
                                    <Box sx={{ width: "28px", height: "28px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>
                                        {initials(r.c.name)}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {r.c.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: "10.5px", color: r.failed ? T.red : T.muted }}>
                                            {r.failed ? "⚠ couldn't load this consultant's visits" : r.teamName}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography sx={{ fontSize: "13px", color: T.ink2 }}>{num(r.booked)}</Typography>
                                <Typography
                                    title="The long-standing measure: days with a visit, against available working days. A half-day visit counts the same as a full day."
                                    sx={{ fontSize: "13px", fontWeight: 700, color: utilColour(r.util) }}
                                >
                                    {r.util == null ? "—" : `${r.util}%`}
                                </Typography>
                                {showPoints && (
                                    <Typography
                                        title={`Capacity points used against ${periodPoints} available (${periodFieldDays.toFixed(1)} field days at ${fmtPoints(config.consultancyFullPoints)} a day). Prices a half-day visit as half a day.`}
                                        sx={{ fontSize: "13px", fontWeight: 700, color: T.purpleDeep }}
                                    >
                                        {r.capacity == null ? "…" : `${Math.round((r.capacity / periodPoints) * 100)}%`}
                                    </Typography>
                                )}
                                <Typography
                                    title={`Visits against ${visitsGoalFor(r.c.jobTitle)} for this period — the target for ${r.c.jobTitle || "this grade"}`}
                                    sx={{ fontSize: "13px", color: T.ink2 }}
                                >
                                    {r.visits == null ? "…" : `${r.visits}/${visitsGoal}`}
                                </Typography>
                                <Typography
                                    title={r.uncategorised ? `${r.uncategorised} visit(s) have no On Site / Remote flag in Salesforce and are not counted here` : undefined}
                                    sx={{ fontSize: "13px", color: T.ink2 }}
                                >
                                    {num(r.physical)}
                                    {!!r.uncategorised && (
                                        <Box component="span" sx={{ fontSize: "10.5px", color: T.muted, fontWeight: 600, ml: "4px" }}>
                                            +{r.uncategorised} unflagged
                                        </Box>
                                    )}
                                </Typography>
                                <Typography sx={{ fontSize: "13px", color: T.ink2 }}>{num(r.remote)}</Typography>
                                <Typography sx={{ fontSize: "13px", color: r.leave ? T.amber : T.muted2 }}>{r.leave}</Typography>
                                <Typography sx={{ fontSize: "13px", color: r.admin ? T.amber : T.muted2 }}>{r.admin}</Typography>
                                {showPoints && (
                                    <Typography sx={{ fontSize: "13px", fontWeight: 800, color: (r.missedDoubles ?? 0) > 0 ? T.amber : T.muted2 }}>
                                        {r.missedDoubles == null ? "…" : r.missedDoubles > 0 ? `⚡ ${r.missedDoubles}` : "0"}
                                    </Typography>
                                )}
                                {showPoints && (
                                    <Typography
                                        title={
                                            r.industryMissing
                                                ? `${r.industryMissing} visit(s) are on accounts with no industry set. They were awarded the default, but the account needs updating in Salesforce.`
                                                : undefined
                                        }
                                        sx={{ fontSize: "13px", fontWeight: 800, color: pointsColour(r.points), fontVariantNumeric: "tabular-nums" }}
                                    >
                                        {r.points == null ? "…" : `${fmtPoints(r.points)}/${pointsGoal}`}
                                        {!!r.industryMissing && (
                                            <Box component="span" sx={{ fontSize: "10px", color: T.amber, fontWeight: 700, ml: "4px" }}>
                                                ⌂{r.industryMissing}
                                            </Box>
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        ))}

                        {/* totals */}
                        <Box sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), gap: "8px", alignItems: "center", padding: "9px 10px", borderTop: `2px solid ${T.border}`, "& > *:not(:first-child)": { textAlign: "right" } }}>
                            <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: T.ink2 }}>Total · {rows.length} consultants</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.ink2 }}>{totals.booked}</Typography>
                            <Typography sx={{ fontSize: "13px", color: T.muted2 }}>—</Typography>
                            {showPoints && (
                                <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.purpleDeep }}>
                                    {rows.length ? `${Math.round((totals.capacity / (rows.length * periodPoints)) * 100)}%` : "—"}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.ink2 }}>
                                {totals.visits}/{Math.round(rows.reduce((t, r) => t + visitsGoalFor(r.c.jobTitle), 0))}
                            </Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.ink2 }}>{totals.physical}</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.ink2 }}>{totals.remote}</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.ink2 }}>{totals.leave}</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.ink2 }}>{totals.admin}</Typography>
                            {showPoints && (
                                <Typography sx={{ fontSize: "13px", fontWeight: 800, color: totals.missedDoubles > 0 ? T.amber : T.ink2 }}>
                                    {totals.missedDoubles > 0 ? `⚡ ${totals.missedDoubles}` : "0"}
                                </Typography>
                            )}
                            {showPoints && (
                                <Typography sx={{ fontSize: "13px", fontWeight: 800, color: totals.points >= rows.length * pointsGoal ? T.green : T.purpleDeep, fontVariantNumeric: "tabular-nums" }}>
                                    {fmtPoints(totals.points)}/{fmtPoints(Math.round(rows.length * pointsGoal * 10) / 10)}
                                </Typography>
                            )}
                        </Box>
                </Box>

                {rows.length > 10 && (
                    <>
                        <ShowAllButton expanded={showAllTable} total={rows.length} onToggle={() => setShowAllTable((v) => !v)} />
                        {!showAllTable && (
                            <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "2px" }}>
                                Showing the first 10 of {rows.length}. The total row and the CSV export cover all of
                                them.
                            </Typography>
                        )}
                    </>
                )}
            </Panel>
            </>
            )}
        </Box>
    );
};

export default ReportsScreen;
